import { useCallback } from 'react'
import { UseToastOptions } from '@chakra-ui/react'

interface ValidationProps {
  currentNumAmalgamFillings: string
  amalgamTotalTeeth: string
  firstAmalgamAge: string
  lastAmalgamAge: string
  currentRootCanals: string
  rootCanalsTotal: string
  firstRootCanalAge: string
  lastRootCanalAge: string
  removedTeeth: string
  firstToothExtractedAge: string
  currentImplants: string
  implantTotalTeeth: string
  firstDentalImplantAge: string
  lastDentalImplantAge: string
}

/**
 * Custom hook for dental form validation
 */
export default function useDentalFormValidation(
  toast: (options: UseToastOptions) => void
) {
  const validateForm = useCallback(
    (formData: ValidationProps): boolean => {
      const {
        currentNumAmalgamFillings,
        amalgamTotalTeeth,
        firstAmalgamAge,
        lastAmalgamAge,
        currentRootCanals,
        rootCanalsTotal,
        firstRootCanalAge,
        lastRootCanalAge,
        removedTeeth,
        firstToothExtractedAge,
        currentImplants,
        implantTotalTeeth,
        firstDentalImplantAge,
        lastDentalImplantAge,
      } = formData

      // Validation 1: Ensure last amalgam removed is blank or greater than first amalgam placed
      if (
        firstAmalgamAge &&
        lastAmalgamAge &&
        Number(lastAmalgamAge) <= Number(firstAmalgamAge)
      ) {
        toast({
          status: 'error',
          title: 'Validation Error',
          description:
            'Estimated age you had your last amalgam removed must be blank or greater than the age you had your first amalgam placed.',
          isClosable: true,
          duration: null,
        })
        return false
      }

      // Validation 2: Ensure total amalgam fillings during life is less than or equal to current amalgam fillings
      if (
        currentNumAmalgamFillings &&
        amalgamTotalTeeth &&
        Number(amalgamTotalTeeth) < Number(currentNumAmalgamFillings)
      ) {
        toast({
          status: 'error',
          title: 'Validation Error',
          description:
            'Estimated total number of teeth with amalgam fillings during your life must be greater than or equal to the current number of amalgam fillings.',
          isClosable: true,
          duration: null,
        })
        return false
      }

      // Validation 3: Ensure first amalgam placed has a value if last amalgam removed has a value
      if (lastAmalgamAge && !firstAmalgamAge) {
        toast({
          status: 'error',
          title: 'Validation Error',
          description:
            'If you provide an estimated age for your last amalgam removed, you must also provide an estimated age for your first amalgam placed.',
          isClosable: true,
          duration: null,
        })
        return false
      }

      // Validation 4: Ensure current amalgam fillings has a value if total amalgam fillings during life has a value
      if (amalgamTotalTeeth && !currentNumAmalgamFillings) {
        toast({
          status: 'error',
          title: 'Validation Error',
          description:
            'If you provide an estimated total number of teeth with amalgam fillings during your life, you must also provide the current number of amalgam fillings.',
          isClosable: true,
          duration: null,
        })
        return false
      }

      // Validation for Root Canals: Ensure last root canal removed is blank or greater than first root canal placed
      if (
        firstRootCanalAge &&
        lastRootCanalAge &&
        Number(lastRootCanalAge) <= Number(firstRootCanalAge)
      ) {
        toast({
          status: 'error',
          title: 'Validation Error',
          description:
            'Estimated age you had your last root canal removed must be blank or greater than the age you had your first root canal placed.',
          isClosable: true,
          duration: null,
        })
        return false
      }

      // Validation for Root Canals: Ensure total root canals during life is less than or equal to current root canals
      if (
        currentRootCanals &&
        rootCanalsTotal &&
        Number(rootCanalsTotal) < Number(currentRootCanals)
      ) {
        toast({
          status: 'error',
          title: 'Validation Error',
          description:
            'Estimated total number of root canals during your life must be greater than or equal to the current number of root canals.',
          isClosable: true,
          duration: null,
        })
        return false
      }

      // Validation for Extractions: Ensure first tooth extracted has a value if total teeth removed has a value
      if (removedTeeth && !firstToothExtractedAge) {
        toast({
          status: 'error',
          title: 'Validation Error',
          description:
            'If you provide a total number of teeth removed, you must also provide the estimated age you had your first tooth extracted.',
          isClosable: true,
          duration: null,
        })
        return false
      }

      // Validation for Implants: Ensure last implant removed is blank or greater than first implant placed
      if (
        firstDentalImplantAge &&
        lastDentalImplantAge &&
        Number(lastDentalImplantAge) <= Number(firstDentalImplantAge)
      ) {
        toast({
          status: 'error',
          title: 'Validation Error',
          description:
            'Estimated age you had your last dental implant removed must be blank or greater than the age you had your first dental implant placed.',
          isClosable: true,
          duration: null,
        })
        return false
      }

      // Validation for Implants: Ensure total implants during life is less than or equal to current implants
      if (
        currentImplants &&
        implantTotalTeeth &&
        Number(implantTotalTeeth) < Number(currentImplants)
      ) {
        toast({
          status: 'error',
          title: 'Validation Error',
          description:
            'Estimated total number of dental implants during your life must be greater than or equal to the current number of dental implants.',
          isClosable: true,
          duration: null,
        })
        return false
      }

      return true
    },
    [toast]
  )

  return { validateForm }
}
